import { type JSX, useState, useEffect } from 'react';
import Typography from 'carbon-react/esm/components/typography';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import Dialog from 'carbon-react/esm/components/dialog';
import { Collaborator } from '../../../../types/collaborator.types';

interface DeleteCollaboratorDialogProps {
  isOpen: boolean;
  collaborator: Collaborator;
  onClose: () => void;
  onConfirm: () => Promise<{ success: boolean; error?: string }>;
  onSuccess?: (message: string) => void;
  onError?: (error: unknown) => void;
}

const DeleteCollaboratorDialog = ({
  isOpen,
  collaborator,
  onClose,
  onConfirm,
  onSuccess,
  onError,
}: DeleteCollaboratorDialogProps): JSX.Element => {
  const [apiError, setApiError] = useState('');

  // Réinitialiser l'erreur à la fermeture de la dialog
  useEffect(() => {
    if (!isOpen) {
      setApiError('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      const result = await onConfirm();
      const collaboratorName = `${collaborator.firstName} ${collaborator.lastName}`;

      if (result.success) {
        const successMessage = `${collaboratorName} a été supprimé avec succès`;
        onClose();
        onSuccess?.(successMessage);
        return;
      }

      const errorMessage =
        result.error || `La suppression du collaborateur '${collaboratorName}' a échoué`;
      setApiError(errorMessage);
      onError?.(new Error(errorMessage));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Une erreur est survenue lors de la suppression du collaborateur';
      setApiError(errorMessage);
      onError?.(err);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onCancel={onClose}
        title="Supprimer un collaborateur"
        aria-label="Supprimer un collaborateur"
      >
        <Box p={4}>
          <Box display={'flex'} flexDirection="column" alignItems="center" gap={2}>
            <Typography>Êtes-vous sûr de vouloir supprimer ce collaborateur ?</Typography>
            <Typography variant="segment-subheader" textAlign="center">
              {collaborator.firstName} {collaborator.lastName} - {collaborator.email}
            </Typography>

            {apiError && (
              <Box mt={2}>
                <Typography variant="p">{apiError}</Typography>
              </Box>
            )}

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={onClose} variantType="secondary" size="small">
                Annuler
              </Button>

              <Button onClick={handleConfirm} variantType="primary" size="small">
                Valider
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default DeleteCollaboratorDialog;
